import { endOfMonth, startOfMonth } from 'date-fns';
import { Profile } from 'helpers/userProfile';
import { t } from 'i18next';
import { Moment } from 'moment-timezone';
import { tickets } from '~/pages/General';
import { EfficiencyCategory } from '~/providers/types/overview';
import { FilterContext } from '../contexts/FilterContext';

export interface FilterStateRoute {
  client?: string;
  units?: string[];
  states?: string[];
  health?: number[];
  category?: string;
}

type ParamType = 'agua' | 'unidades' | 'energia' | 'maquinas' | 'ambientes';

type AnalysisFilterOption<Value = number> = { value: Value, name: string };
type AnalysisSelectedOption = {
  label: string;
  values: AnalysisFilterOption[]
}

export function handleFilterStateRoute(
  client: string | undefined,
  selectedOptions: FilterContext['selectedOptions'],
  profile: Profile,
  health?: number[],
): FilterStateRoute {
  const filter: FilterStateRoute = {};

  if (selectedOptions.group?.units.length) {
    filter.units = selectedOptions.group.units.map((unit) => unit.toString());
  }

  if (selectedOptions.state?.length) {
    filter.states = selectedOptions.state;
  }

  if (profile.manageMultipleClients && client) {
    filter.client = client;
  }

  if (health?.length) {
    filter.health = health;
  }

  return filter;
}

export function handleOverviewStateFilters(
  filterState: FilterStateRoute | undefined,
  state: {
    selectedState: string[],
    selectedUnit: string[],
    selectedHealth: number[],
    selectedClientFilter: string[],
    startDate?: Date | Moment | null,
    endDate?: Date | Moment | null,
    energyEfficiency?: string[]
  },
  render: () => void,
  paramType?: string,
): void {
  // TODO - Adicionar outros cards de parâmetros
  if (!filterState || !isParamType(paramType)) return;

  if (filterState.client) {
    state.selectedClientFilter = [filterState.client];
  }

  if (filterState.states?.length) {
    state.selectedState = filterState.states;
  }

  if (filterState.units?.length) {
    state.selectedUnit = filterState.units;
  }

  if (filterState.health?.length) {
    state.selectedHealth = filterState.health;
  }

  if (filterState.category) {
    state.energyEfficiency = [filterState.category];
  }

  if (paramType === 'energia') {
    state.endDate = endOfMonth(new Date());
    state.startDate = startOfMonth(new Date());
  }

  render();
}

function isParamType(type: string | undefined): type is ParamType {
  return !!type && (type === 'agua' || type === 'unidades' || type === 'energia' || type === 'maquinas' || type === 'ambientes');
}

export function handleFiltersAnalysis(filters: {
  clients: AnalysisFilterOption[],
  states: AnalysisFilterOption[],
  units: AnalysisFilterOption[],
  health: AnalysisFilterOption[],
}, state: {
    selectedState: string[],
    selectedUnit: string[],
    selectedClientFilter: string[],
    selectedHealth: number[],
    energyEfficiency?: string[]
  }): {
  clients: AnalysisSelectedOption,
  states: AnalysisSelectedOption,
  units: AnalysisSelectedOption,
  health: AnalysisSelectedOption,
} {
  const energyEfficiencyValues = state.energyEfficiency
    ? tickets
      .filter((ticket) => {
        const label = ticket.label.toUpperCase();
        return state.energyEfficiency?.includes(label);
      })
      .map((ticket) => {
        const label = ticket.label.toUpperCase();

        return {
          ...ticket,
          name: label,
          value: label,
        };
      })
    : undefined;

  return {
    clients: {
      label: t('cliente'),
      values: filters.clients.filter((item) => state.selectedClientFilter.includes(String(item.value))),
    },
    states: {
      label: t('estado'),
      values: filters.states.filter((item) => state.selectedState.includes(String(item.value))),
    },
    units: {
      label: t('unidade'),
      values: filters.units.filter((item) => state.selectedUnit.includes(String(item.value))),
    },
    health: {
      label: t('saudeAtual'),
      values: filters.health.filter((item) => state.selectedHealth.includes(item.value)),
    },
    ...(energyEfficiencyValues && {
      energyEfficiency: {
        label: 'Efic. Energética',
        values: energyEfficiencyValues,
      },
    }),
  };
}

export function handleEfficiencyRatingFilter(
  client: string | undefined,
  selectedOptions: FilterContext['selectedOptions'],
  profile: Profile,
  category: EfficiencyCategory,
): FilterStateRoute {
  const filter = handleFilterStateRoute(client, selectedOptions, profile);

  filter.category = category;

  return filter;
}
